import { useEffect, useState } from "react";
import Sidebar from "@/components/Sidebar";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import {
  BookOpen, Rocket, Upload, Wand2, Send, CreditCard, Zap, ShieldCheck,
  FileSpreadsheet, ImageIcon, AlertCircle, ExternalLink, Loader2, Lock,
} from "lucide-react";

export default function Manual() {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/billing/subscription");
        setSub(data);
      } catch {
        setSub({ status: "none" });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const status = sub?.status || "none";
  const hasAccess = status === "active" || status === "trialing";

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#F8F9FA]">
      <Sidebar />
      <main className="flex-1 p-6 sm:p-10">
        <header className="mb-8">
          <div className="eyebrow mb-2">Documentação</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#09090B] flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-[#002FA7]" />
            Manual de Uso · Magic SIP
          </h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Guia completo do fluxo: da planilha até a publicação em massa no Nuvemshop.
          </p>
        </header>

        {loading && (
          <div className="text-sm text-zinc-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Verificando assinatura…
          </div>
        )}

        {!loading && !hasAccess && (
          <div
            className="border border-amber-200 bg-amber-50 rounded-md p-8 max-w-2xl text-center"
            data-testid="manual-locked-banner"
          >
            <Lock className="w-10 h-10 text-amber-600 mx-auto mb-3" />
            <div className="font-display text-xl font-bold text-amber-900 mb-2">
              Manual disponível apenas para assinantes
            </div>
            <p className="text-sm text-amber-900/80 mb-5">
              O manual de uso detalhado do Magic SIP fica acessível para usuários com
              plano ativo ou trial em andamento. Comece agora com 7 dias grátis.
            </p>
            <Button
              onClick={() => navigate("/pricing")}
              className="brand-primary text-white rounded-md h-10 px-6"
              data-testid="manual-cta-pricing"
            >
              <CreditCard className="w-4 h-4 mr-2" /> Ver planos
            </Button>
          </div>
        )}

        {!loading && hasAccess && (
          <article
            className="prose prose-zinc max-w-3xl bg-white border border-zinc-200 rounded-md p-8"
            data-testid="manual-content"
          >
            <Section icon={<Rocket />} title="1. Visão geral">
              <p>
                O <strong>Magic SIP</strong> automatiza o cadastro em massa de produtos no seu
                e-commerce (Nuvemshop, Bling, Tiny, Omie). Você fornece uma planilha com{" "}
                <strong>Nome</strong>, <strong>Categoria</strong> e <strong>Preço</strong>; a IA
                busca imagens em catálogos brasileiros, gera descrição SEO-otimizada, indicação
                de uso, SKU e publica com um clique.
              </p>
            </Section>

            <Section icon={<FileSpreadsheet />} title="2. Preparando a planilha">
              <p>Suporta CSV e XLSX. Colunas mínimas obrigatórias:</p>
              <ul>
                <li><code>nome</code> · nome comercial do produto</li>
                <li><code>categoria</code> · segmento (ex.: medicamento, alimento, construcao)</li>
                <li><code>preco</code> · valor cheio em BRL (ex.: 89,90 ou 89.90 ou R$ 89,90)</li>
              </ul>
              <p>Colunas opcionais (se presentes, pulam a IA e usam o valor da planilha):</p>
              <ul className="text-sm">
                <li><code>preco_promocional</code>, <code>sku</code>, <code>descricao</code>,{" "}
                <code>seo_title</code>, <code>seo_description</code>, <code>indicacao</code>,{" "}
                <code>imagem</code> (URLs separadas por <code>;</code>), <code>estoque</code>,{" "}
                <code>altura</code>, <code>largura</code>, <code>comprimento</code>, <code>peso</code></li>
              </ul>
              <p>
                Baixe o modelo pronto pelo botão <strong>Modelo CSV</strong> na tela{" "}
                <strong>Publicar → Lote</strong>.
              </p>
            </Section>

            <Section icon={<Wand2 />} title="3. Executando o batch">
              <ol>
                <li>Vá em <strong>Publicar</strong> → aba <strong>Lote (até 1000)</strong>.</li>
                <li>Cole os produtos ou faça upload da planilha.</li>
                <li>
                  Clique em <strong>Preparar</strong>. A IA vai:
                  <ul>
                    <li>Buscar imagens em catálogos VTEX (Drogaria SP, Kabum, Leroy Merlin, etc.);</li>
                    <li>Filtrar imagens irrelevantes via Vision (Gemini);</li>
                    <li>Gerar descrição HTML, SEO, SKU e preço sugerido nos campos vazios.</li>
                  </ul>
                </li>
                <li>Revise a tabela editável (marque/desmarque linhas, edite preços).</li>
                <li>
                  Clique em <strong>Publicar selecionados</strong>. Ao final, um botão{" "}
                  <strong>Apagar publicados</strong> aparece caso queira desfazer o lote.
                </li>
              </ol>
              <p className="text-xs text-zinc-500">
                Dica: se navegar para outra aba, o batch continua rodando em segundo plano (persistência via localStorage).
              </p>
            </Section>

            <Section icon={<ImageIcon />} title="4. Catálogos suportados por categoria">
              <ul className="text-sm">
                <li><strong>Farmácia/Beleza:</strong> Drogaria SP, Pacheco, Pague Menos, São João</li>
                <li><strong>Eletrônicos:</strong> Kabum, Ricardo Eletro</li>
                <li><strong>Pet:</strong> Cobasi</li>
                <li><strong>Supermercado/Alimentos/Limpeza:</strong> Zona Sul, Bistek, Mambo, SuperMuffato + Open Food Facts</li>
                <li><strong>Materiais de Construção:</strong> Leroy Merlin, Telhanorte, C&C</li>
              </ul>
              <p className="text-xs text-zinc-500">
                Se nenhum catálogo retornar imagem para uma categoria específica, o Magic SIP
                marca a linha como <strong>SEM IMAGEM</strong> — você pode adicionar URLs manualmente na planilha.
              </p>
            </Section>

            <Section icon={<Send />} title="5. Integração com ERPs">
              <p>Em <strong>Configurações</strong>, conecte seu ERP:</p>
              <ul className="text-sm">
                <li><strong>Nuvemshop</strong> — OAuth 1-clique (recomendado). Publica produtos completos com imagens e SEO.</li>
                <li>
                  <strong>Bling v3</strong> — OAuth 1-clique com renovação automática de token.
                  Envia URLs de imagens externas. Consulte o{" "}
                  <a
                    href="/manual/bling"
                    className="text-[#002FA7] underline font-semibold"
                    data-testid="manual-bling-link"
                  >
                    manual detalhado de integração Bling
                  </a>{" "}
                  para o passo a passo completo.
                </li>
                <li><strong>Tiny v2</strong> — Token API manual (Configurações → E-commerce → Token API).</li>
                <li><strong>Omie</strong> — App Key + App Secret. Faz upload real das imagens (base64).</li>
              </ul>
              <div className="mt-3">
                <a href="/manual/bling">
                  <button
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md brand-primary text-white text-sm font-medium hover:opacity-90"
                    data-testid="manual-bling-cta-button"
                  >
                    Abrir Manual de Integração Bling →
                  </button>
                </a>
              </div>
            </Section>

            <Section icon={<CreditCard />} title="6. Planos e cobrança">
              <p>
                Assinatura mensal via Mercado Pago. Comece com <strong>7 dias grátis</strong>{" "}
                (cartão obrigatório apenas para validar identidade, sem cobrança) e publique até
                10 produtos no trial.
              </p>
              <ul className="text-sm">
                <li>Micro · R$ 8,90 · até 100 produtos/mês</li>
                <li>Start · R$ 89 · até 1.000</li>
                <li>Pro · R$ 169 · até 2.000</li>
                <li>Business · R$ 349 · até 5.000</li>
                <li>Enterprise · R$ 699 · até 10.000</li>
              </ul>
              <p className="text-xs text-zinc-500">
                Se sua assinatura for cancelada ou ficar inadimplente, os produtos publicados via
                Magic SIP têm as imagens/descrições substituídas por um placeholder — restauradas
                automaticamente ao renovar. Nada é apagado.
              </p>
            </Section>

            <Section icon={<ShieldCheck />} title="7. Segurança & LGPD">
              <p>
                Todas as credenciais de ERP (tokens, secrets) são criptografadas em repouso com
                Fernet. Nenhum dado sensível de cliente da sua loja é armazenado — apenas o token
                OAuth do lojista + IDs dos produtos publicados.
              </p>
              <p>
                Os 3 webhooks LGPD obrigatórios do Nuvemshop (<code>store/redact</code>,{" "}
                <code>customers/redact</code>, <code>customers/data_request</code>) estão
                implementados. Veja em <strong>Configurações → Webhooks LGPD</strong>.
              </p>
            </Section>

            <Section icon={<AlertCircle />} title="8. Erros comuns">
              <ul className="text-sm">
                <li>
                  <strong>Sem imagem para vários itens</strong> — categoria não bate com nenhum
                  catálogo. Ajuste a coluna <code>categoria</code> ou envie URLs próprias.
                </li>
                <li>
                  <strong>402 Payment Required no publish</strong> — trial esgotado ou assinatura
                  vencida. Veja em <strong>Configurações → Assinatura</strong>.
                </li>
                <li>
                  <strong>Saldo LLM Key esgotado</strong> — a IA de descrição/SEO usa o Emergent
                  LLM Key. Recarregue em Profile → Universal Key.
                </li>
              </ul>
            </Section>

            <Section icon={<ExternalLink />} title="9. Suporte">
              <p>
                Dúvidas? Fale conosco pelos canais oficiais:
              </p>
              <ul className="text-sm">
                <li>
                  <strong>WhatsApp</strong>:{" "}
                  <a href="https://w.app/carlos_soglia" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">
                    w.app/carlos_soglia
                  </a>
                </li>
                <li>
                  <strong>E-mail</strong>:{" "}
                  <a href="mailto:suporte@magicsearchimagesproducts.com" className="text-[#002FA7] underline">
                    suporte@magicsearchimagesproducts.com
                  </a>
                </li>
                <li>
                  <strong>Central de atendimento</strong>:{" "}
                  <a href="/support" className="text-[#002FA7] underline">/support</a>
                </li>
              </ul>
              <p className="text-xs text-zinc-500">Resposta em até 24h úteis.</p>
            </Section>
          </article>
        )}
      </main>
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <section className="not-prose mb-8">
      <h2 className="flex items-center gap-2 font-display text-lg font-bold text-[#09090B] mb-3">
        <span className="w-6 h-6 rounded-md brand-primary text-white flex items-center justify-center [&_svg]:w-3.5 [&_svg]:h-3.5">
          {icon}
        </span>
        {title}
      </h2>
      <div className="text-sm text-zinc-700 leading-relaxed space-y-2 [&_ul]:list-disc [&_ul]:ml-5 [&_ol]:list-decimal [&_ol]:ml-5 [&_code]:bg-zinc-100 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:font-mono [&_code]:text-xs [&_code]:text-[#002FA7]">
        {children}
      </div>
    </section>
  );
}
